import clsx from 'clsx';
import { EqualIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import InformationIcon from '@/assets/icons/information.svg';
import { CloseIcon } from '@/components/icons/close';
import { MinusIcon } from '@/components/icons/minus';
import { ReturnBackArrowIcon } from '@/components/icons/return-back-arrow';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useEditCampaignContext } from '@/contexts/edit-campaign-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { useFormattedDays } from './use-formatted-days';

export function Calculation() {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { hookForm, hiringTarget } = useEditCampaignContext();
  const formatDaysByBudgetType = useFormattedDays();

  const { targetBudget, projectedBudget, overlapDuration, preHiringGoalSpend } =
    hiringTarget;

  const { watch } = hookForm;
  const inputBudget = watch('budget') || 0;
  const budgetType = watch('budget_type');

  const isLifetimeBudget = budgetType === 'lifetime' || !budgetType;

  const spendTitle = useMemo(() => {
    switch (budgetType) {
      case 'weekly':
        return intl.formatMessage({ defaultMessage: 'Spend per week' });
      case 'monthly':
        return intl.formatMessage({ defaultMessage: 'Spend per month' });
      case 'daily':
        return intl.formatMessage({ defaultMessage: 'Spend per day' });
      case 'lifetime':
      default:
        return intl.formatMessage({ defaultMessage: 'Campaign budget' });
    }
  }, [budgetType, intl]);

  // Hiring target for lifetime budget is the input budget minus the pre-hiring goal spend
  if (isLifetimeBudget)
    return (
      <>
        <ReturnBackArrowIcon />

        <Metric label={spendTitle} value={formatCurrency(inputBudget)} />

        <MinusIcon className="text-fx-primary mt-0.5" />

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Spent before hiring goal',
          })}
          value={formatCurrency(preHiringGoalSpend)}
        />

        <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />

        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Target budget',
          })}
          value={formatCurrency(targetBudget)}
        />
      </>
    );

  // Hiring target for non-lifetime budget is the per day budget multiplied by the overlap duration plus the budget spent
  return (
    <>
      <ReturnBackArrowIcon />

      <Metric label={spendTitle} value={formatCurrency(inputBudget)} />

      <CloseIcon className="text-fx-primary mt-0.5" />

      <span className="flex items-start gap-1">
        <Metric
          label={intl.formatMessage({
            defaultMessage: 'Duration',
          })}
          value={formatDaysByBudgetType(overlapDuration(), budgetType)}
          info={
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Target duration is the duration of the campaign that overlaps with the active hiring goal.',
              })}
            >
              <img src={InformationIcon} alt="information" />
            </Tooltip>
          }
        />
      </span>

      <EqualIcon className="text-fx-primary mt-0.5 h-4 w-4" />

      <Metric
        label={intl.formatMessage({
          defaultMessage: 'Projected spend',
        })}
        value={formatCurrency(projectedBudget)}
      />
    </>
  );
}

function Metric({
  label,
  value,
  className,
  info,
}: {
  label: string;
  value: string;
  className?: string;
  info?: React.ReactNode;
}) {
  return (
    <div className={clsx('flex flex-col gap-1 whitespace-nowrap', className)}>
      <div className="flex items-center gap-1">
        <Typography variant="subtitle2">{label}</Typography>
        {info}
      </div>
      <Typography variant="body1">{value}</Typography>
    </div>
  );
}
